import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard, Store, KeyRound, Database, HardDriveDownload, ArchiveRestore,
    MoonStar, Settings, LogOut, Menu, X, ArrowLeftRight, Image,
} from 'lucide-react';
import useAuthStore from '../store/authStore';

// Naviqasiya qrupları
const NAV_GROUPS = [
    {
        title: 'Ümumi',
        items: [
            { key: 'dashboard',   Icon: LayoutDashboard,   label: 'Dashboard',          href: '/superadmin/dashboard' },
            { key: 'tenants',     Icon: Store,             label: 'Restoranlar',        href: '/superadmin/tenants' },
            { key: 'impersonate', Icon: KeyRound,          label: 'Tenantlara giriş',   href: '/superadmin/impersonate' },
        ],
    },
    {
        title: 'Baza',
        items: [
            { key: 'import',  Icon: Database,          label: 'Baza import',        href: '/superadmin/import' },
            { key: 'importv2', Icon: ArrowLeftRight,   label: 'İdxal / İxrac v2',   href: '/superadmin/import-v2', badge: 'yeni' },
            { key: 'backup',  Icon: HardDriveDownload, label: 'Baza backup',        href: '/superadmin/backup' },
            { key: 'restore', Icon: ArchiveRestore,    label: 'Baza restore',       href: '/superadmin/restore' },
            { key: 'nightly', Icon: MoonStar,          label: 'Gecə backup-ları',   href: '/superadmin/nightly' },
        ],
    },
    {
        title: 'Sistem',
        items: [
            { key: 'settings', Icon: Settings, label: 'Ayarlar', href: '/superadmin/settings' },
            { key: 'login-bg', Icon: Image,    label: 'Giriş ekranı fon şəkli', href: '/superadmin/login-bg' },
        ],
    },
];

const initials = (name) => String(name || 'SA').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || 'SA';

const SuperadminLayout = ({ children, title, subtitle, actions }) => {
    const { user, logout } = useAuthStore();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    // '/superadmin/import' '/superadmin/import-v2'-ni də tutmasın deyə: tam və ya alt yol
    const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

    // Mobil menyu açıq olanda arxa səhifə sürüşməsin
    useEffect(() => {
        document.body.style.overflow = open ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    const SidebarBody = () => (
        <div className="relative flex h-full flex-col overflow-hidden">
            {/* yumşaq brend işığı */}
            <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full bg-[#0090bb]/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-32 -right-20 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />

            <div className="relative flex items-center justify-between gap-2 px-5 pb-5 pt-6">
                <img src="/FiscalPOS_white.png" alt="FiscalPOS" className="h-9 w-auto max-w-[150px] object-contain" />
                <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-sky-200/80">
                    Superadmin
                </span>
            </div>

            <nav className="relative flex-1 space-y-6 overflow-y-auto px-3 py-2">
                {NAV_GROUPS.map((g) => (
                    <div key={g.title}>
                        <div className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{g.title}</div>
                        <div className="space-y-0.5">
                            {g.items.map(({ key, Icon, label, href, badge }) => {
                                const active = isActive(href);
                                return (
                                    <button
                                        key={key}
                                        onClick={() => { navigate(href); setOpen(false); }}
                                        className={`group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
                                            active
                                                ? 'bg-white/[0.08] text-white'
                                                : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-100'
                                        }`}
                                    >
                                        {active && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-[#22b8e6]" />}
                                        <Icon className={`h-[18px] w-[18px] flex-shrink-0 ${active ? 'text-[#4cc9f0]' : 'text-slate-500 group-hover:text-slate-300'}`} strokeWidth={2} />
                                        <span className="truncate">{label}</span>
                                        {badge && (
                                            <span className="ml-auto rounded-md bg-[#22b8e6]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#4cc9f0] ring-1 ring-[#22b8e6]/25">{badge}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </nav>

            <div className="relative border-t border-white/[0.06] p-3">
                <div className="flex items-center gap-3 rounded-xl bg-white/[0.04] p-2.5">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#0090bb] to-indigo-500 text-xs font-bold text-white">
                        {initials(user?.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-slate-100">{user?.name || 'Super Admin'}</div>
                        <div className="text-[11px] text-slate-500">Superadmin</div>
                    </div>
                    <button
                        onClick={() => logout(navigate)}
                        title="Çıxış"
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-500/15 hover:text-red-300"
                    >
                        <LogOut className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Masaüstü yan menyu */}
            <aside className="fixed bottom-0 left-0 top-0 z-30 hidden w-64 flex-col bg-[#0b1220] md:flex">
                <SidebarBody />
            </aside>

            {/* Mobil yan menyu */}
            {open && (
                <div className="fixed inset-0 z-40 flex md:hidden" onClick={() => setOpen(false)}>
                    <aside className="flex w-72 flex-col bg-[#0b1220] shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setOpen(false)} className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-white/10" aria-label="Bağla">
                            <X className="h-4 w-4" />
                        </button>
                        <SidebarBody />
                    </aside>
                    <div className="flex-1 bg-slate-950/50 backdrop-blur-sm" />
                </div>
            )}

            {/* Əsas hissə */}
            <div className="md:pl-64">
                <header className="sticky top-0 z-20 border-b border-slate-200/70 bg-white/80 backdrop-blur-xl">
                    <div className="flex items-center gap-3 px-4 py-3.5 sm:px-8">
                        <button
                            onClick={() => setOpen(true)}
                            className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 md:hidden"
                            aria-label="Menyu"
                        >
                            <Menu className="h-4 w-4" />
                        </button>
                        <div className="min-w-0 flex-1">
                            {title && <h1 className="truncate text-lg font-bold tracking-tight text-slate-900 sm:text-xl">{title}</h1>}
                            {subtitle && <p className="truncate text-xs text-slate-500 sm:text-[13px]">{subtitle}</p>}
                        </div>
                        {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
                    </div>
                </header>

                <main className="px-4 py-6 sm:px-8 sm:py-8">
                    <div className="mx-auto max-w-7xl">
                        {children}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default SuperadminLayout;
